import { Button, Spinner } from '@consigli/facade';
import { type ActionItem, type ProjectParticipant } from '@consigli/types';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { ActionItemsError } from './action-items-error';
import { ActionItemsTable, type ActionItemColumn } from './action-items-table';
import { CopyButton } from './copy-button';
import { CreateActionItemForm } from './create-action-item-form';
import { TranscriptSourceModal } from './transcript-source-modal';
import { useActionItemMutations } from '../hooks/use-action-item-mutations';
import { useTopics } from '../hooks/use-topics';
import { formatActionItemsForCopy } from '../utils';
import { resolveResponsibleParty } from '../utils/participant-utils';

import type { SupportedItem } from './action-item-types';
import type { TranscriptSourceMeeting } from './transcript-source-modal';
import type { ParsedTranscriptEntry } from '../utils/export-types';

// Columns for meeting-level view (no 'meeting' column since we're already in a meeting)
const MEETING_COLUMNS: ActionItemColumn[] = ['title', 'deadline', 'topic', 'status'];

interface ActionItemsTabProps {
  loading: boolean;
  generating: boolean;
  error: string | null;
  actionItems: ActionItem[];
  hasTranscripts: boolean;
  transcriptEntries?: ParsedTranscriptEntry[];
  transcriptMeeting?: TranscriptSourceMeeting;
  participants: ProjectParticipant[];
  meetingId: string;
  onRetry: () => void;
  onActionItemsChange: (items: ActionItem[]) => void;
}

export const ActionItemsTab: FC<ActionItemsTabProps> = ({
  loading,
  generating,
  error,
  actionItems,
  hasTranscripts,
  transcriptEntries,
  transcriptMeeting,
  participants,
  meetingId,
  onRetry,
  onActionItemsChange,
}) => {
  const { t } = useTranslation();

  const [expandedPersons, setExpandedPersons] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [sourceItem, setSourceItem] = useState<SupportedItem | null>(null);

  const { allTopics, handleAddCustomTopic } = useTopics({ items: actionItems });

  // For copy button - group by person
  const groupedActions = useMemo(
    () =>
      (Array.isArray(actionItems) ? actionItems : []).reduce<Record<string, ActionItem[]>>(
        (acc, item) => {
          const person =
            item.responsiblePerson?.name ??
            t(
              'meeting-secretary.meetings.responsible-person-unclear',
              'responsible person, clarification needed',
            );
          return { ...acc, [person]: [...(acc[person] ?? []), item] };
        },
        {},
      ),
    [actionItems, t],
  );

  const { deleteItem, bulkReassignItems, bulkUpdateStatus, bulkUpdateDeadline, bulkAddTopic } =
    useActionItemMutations(meetingId);

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedPersons((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const handleItemUpdate = useCallback(
    (updated: ActionItem) => {
      onActionItemsChange(actionItems.map((ai) => (ai.id === updated.id ? updated : ai)));
    },
    [actionItems, onActionItemsChange],
  );

  const handleItemDelete = useCallback(
    async (item: ActionItem) => {
      try {
        await deleteItem(item.id);
        onActionItemsChange(actionItems.filter((ai) => ai.id !== item.id));
      } catch {
        toast.error(t('meeting-secretary.meetings.error-deleting-item'));
      }
    },
    [actionItems, deleteItem, onActionItemsChange, t],
  );

  // Bulk reassign items by their IDs using transactional backend endpoint
  // All succeed or all fail - no partial state
  // Bulk reassign items - receives itemIds directly from table
  const handleBulkReassignItems = useCallback(
    async (
      itemIds: string[],
      target: { toMeetingUserId?: string | null; toCustomPartyName?: string | null },
    ) => {
      if (itemIds.length === 0) return;

      try {
        await bulkReassignItems(itemIds, target);

        const responsiblePerson = resolveResponsibleParty(
          { meetingUserId: target.toMeetingUserId, customPartyName: target.toCustomPartyName },
          participants,
        );

        onActionItemsChange(
          actionItems.map((item) =>
            itemIds.includes(item.id) ? { ...item, responsiblePerson } : item,
          ),
        );

        toast.success(
          t('meeting-secretary.action-items.items-reassigned', { count: itemIds.length }),
        );
      } catch {
        toast.error(t('meeting-secretary.action-items.bulk-reassign-error'));
      }
    },
    [actionItems, bulkReassignItems, onActionItemsChange, participants, t],
  );

  // Bulk update status - receives itemIds directly from table
  const handleBulkUpdateStatus = useCallback(
    async (itemIds: string[], status: string) => {
      if (itemIds.length === 0) return;

      try {
        await bulkUpdateStatus(itemIds, status);
        onActionItemsChange(
          actionItems.map((item) =>
            itemIds.includes(item.id) ? { ...item, completionStatus: status } : item,
          ),
        );
        toast.success(
          t('meeting-secretary.action-items.items-status-updated', { count: itemIds.length }),
        );
      } catch {
        toast.error(t('meeting-secretary.action-items.bulk-update-error'));
      }
    },
    [actionItems, bulkUpdateStatus, onActionItemsChange, t],
  );

  // Bulk update deadline - receives itemIds directly from table
  const handleBulkUpdateDeadline = useCallback(
    async (itemIds: string[], deadline: string | null) => {
      if (itemIds.length === 0) return;

      try {
        await bulkUpdateDeadline(itemIds, deadline);
        onActionItemsChange(
          actionItems.map((item) =>
            itemIds.includes(item.id) ? { ...item, dueDate: deadline } : item,
          ),
        );
        toast.success(
          t('meeting-secretary.action-items.items-deadline-updated', { count: itemIds.length }),
        );
      } catch {
        toast.error(t('meeting-secretary.action-items.bulk-update-error'));
      }
    },
    [actionItems, bulkUpdateDeadline, onActionItemsChange, t],
  );

  // Bulk add topic - receives itemIds directly from table
  const handleBulkAddTopic = useCallback(
    async (itemIds: string[], topic: string) => {
      if (itemIds.length === 0) return;

      try {
        await bulkAddTopic(itemIds, topic);
        onActionItemsChange(
          actionItems.map((item) => (itemIds.includes(item.id) ? { ...item, topic } : item)),
        );
        toast.success(
          t('meeting-secretary.action-items.items-topic-added', { count: itemIds.length }),
        );
      } catch {
        toast.error(t('meeting-secretary.action-items.bulk-update-error'));
      }
    },
    [actionItems, bulkAddTopic, onActionItemsChange, t],
  );

  const isIdle = !loading && !generating && !error;

  return (
    <div className="space-y-4">
      {(loading || generating) && (
        <div className="flex items-center justify-center py-4">
          <Spinner size="xsmall" />
          <span className="ml-2 text-gray-600 text-sm">
            {loading
              ? t('meeting-secretary.meetings.loading-action-items')
              : t('meeting-secretary.meetings.generating-action-items')}
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <ActionItemsError error={error} hasTranscripts={hasTranscripts} onRetry={onRetry} />
      )}

      {/* No transcripts */}
      {isIdle && actionItems.length === 0 && !hasTranscripts && (
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-4">{'\u{1F4DD}'}</div>
          <p className="text-gray-500">
            {t('meeting-secretary.meetings.no-transcripts-available')}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {t('meeting-secretary.meetings.no-transcripts-action-items-required')}
          </p>
        </div>
      )}

      {/* Transcripts exist but no items yet */}
      {isIdle && actionItems.length === 0 && hasTranscripts && (
        <p className="text-gray-500 text-sm">
          {t('meeting-secretary.meetings.no-action-items-available')}
        </p>
      )}

      {/* Action items table */}
      {isIdle && actionItems.length > 0 && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <CopyButton textToCopy={formatActionItemsForCopy(groupedActions)} />
          </div>
          <ActionItemsTable
            items={actionItems}
            columns={MEETING_COLUMNS}
            groupBy="person"
            participants={participants}
            topics={allTopics}
            meetingId={meetingId}
            expandedGroups={expandedPersons}
            onToggleGroup={toggleGroup}
            onUpdate={(item) => handleItemUpdate(item as ActionItem)}
            onDelete={(item) => handleItemDelete(item as ActionItem)}
            onAddCustomTopic={handleAddCustomTopic}
            onShowSource={transcriptEntries?.length ? setSourceItem : undefined}
            onBulkReassignItems={handleBulkReassignItems}
            onBulkUpdateStatus={handleBulkUpdateStatus}
            onBulkUpdateDeadline={handleBulkUpdateDeadline}
            onBulkAddTopic={handleBulkAddTopic}
          />
        </div>
      )}

      <TranscriptSourceModal
        isOpen={!!sourceItem}
        onClose={() => setSourceItem(null)}
        meeting={transcriptMeeting}
        entries={transcriptEntries ?? []}
        segmentIds={sourceItem?.sourceSegmentIds ?? []}
      />

      <CreateActionItemForm
        isOpen={isCreating && hasTranscripts}
        meetingId={meetingId}
        participants={participants}
        onCreated={() => {
          onRetry();
        }}
        onCancel={() => setIsCreating(false)}
      />

      {/* Add button */}
      {hasTranscripts && isIdle && (
        <div className="flex justify-end pt-2">
          <Button
            primary
            rounded
            icon={FaPlus}
            iconSize={10}
            className="!px-2.5 !py-1.5 !text-xs"
            onClick={() => setIsCreating(true)}
          >
            {t('meeting-secretary.meetings.add-action-item', 'Add Action Item')}
          </Button>
        </div>
      )}
    </div>
  );
};
